<template>
  <component
    v-if="show"
    :is="component"
    :class="{
      'header-link': true,
      [`header-link--${id}`]: true,
      'header-link--active': active,
      'header-link--main': isMain,
      'header-link--cta': isCta,
      'js-open-signup-popup': openSignupPopup,
      'js-open-login-popup': loginPopup && hasJsPopupClass
    }"
    :href="computedHref"
    v-bind="dataAttributes"
    @click="onClick">
    <span v-html="linkCopy" />
    <sprite
      v-if="menuSprite"
      class="header-link__menu-sprite"
      id="app/arrow-down-small" />
  </component>
</template>

<script>
import { mapGetters } from "vuex";
import events from "@/events";
import globals from "@/utils/globals";
import Button from "@/shared/button";
import Sprite from "@/shared/sprite";
import "/wds/sprites/app/arrow-down-small.svg";

// Define empty routes object for ssr
const routes = globals.routes || {};

export default {
  name: "HeaderLink",
  components: {
    Sprite
  },
  hrefs: {
    login: routes.login,
    signup: routes.signup,
    help: routes.help,
    invite: routes.referral,
    settings: routes.settings,
    wishlist: routes.wishlist,
    logout: routes.logout,
    pricing: routes.plans,
    discover: routes.books,
    highlights: routes.highlights,
    library: routes.library,
    upgrade: routes.plans,
    business: `${routes.business}?utm_source=homepage&utm_medium=link&utm_campaign=header`
  },
  props: {
    id: { type: String, default: null },
    onlyLogin: { type: Boolean, default: false },
    onlyLogout: { type: Boolean, default: false },
    onlyPremium: { type: Boolean, default: false },
    onlyBasic: { type: Boolean, default: false },
    menuSprite: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    isMain: { type: Boolean, default: false },
    useRouter: { type: Boolean, default: false },
    hasJsPopupClass: { type: Boolean, default: true },
    href: { type: String, default: null },
    data: { type: Object, default: () => {} }
  },
  computed: {
    ...mapGetters(["isUserLoggedIn", "isUserLoggedOut", "isUserPremium", "isUserBasic"]),
    ...mapGetters("layout", ["startTrialButtonCtaHref"]),
    openSignupPopup() {
      if (this.startTrialButtonCtaHref) {
        return false;
      }

      return this.signupPopup && this.hasJsPopupClass;
    },
    show() {
      if (this.onlyLogin && !this.isUserLoggedIn) {
        return false;
      }
      if (this.onlyLogout && !this.isUserLoggedOut) {
        return false;
      }
      if (this.onlyPremium && !this.isUserPremium) {
        return false;
      }
      if (this.onlyBasic && !this.isUserBasic) {
        return false;
      }
      return true;
    },
    isCta() {
      return ["signup", "upgrade"].includes(this.id);
    },
    component() {
      if (this.isCta) {
        return Button;
      }
      if (this.computedHref) {
        return "a";
      }
      return "button";
    },
    eventName() {
      return `header-20200608-${this.id.replace(/-/g, "_")}_link`;
    },
    loginPopup() {
      return ["login"].includes(this.id);
    },
    signupPopup() {
      return ["signup"].includes(this.id);
    },
    linkCopy() {
      return this.$t(this.id);
    },
    computedHref() {
      if (this.id === "signup" && this.startTrialButtonCtaHref) {
        return this.startTrialButtonCtaHref;
      }

      return this.href || this.$options.hrefs[this.id];
    },
    dataAttributes() {
      if (!this.data) {
        return {};
      }
      return Object.entries(this.data).reduce((object, value) => {
        object[`data-${value[0]}`] = value[1];
        return object;
      }, {});
    }
  },
  methods: {
    onClick(e) {
      this.$emit("click", e);
      events.track(this.eventName, "1-click");
    }
  },
  i18n: {
    messages: {
      en: {
        login: "Log in",
        signup: "Start free trial",
        signupVariation: "Try it free",
        pricing: "Pricing",
        "user-menu": "Account",
        help: "Help&nbsp;&&nbsp;Support",
        invite: "Invite&nbsp;Friends",
        logout: "Logout",
        settings: "Settings",
        wishlist: "Wishlist",
        library: "My Library",
        discover: "Explore",
        highlights: "Highlights",
        upgrade: "Upgrade to Premium",
        business: "For business"
      },
      de: {
        login: "Einloggen",
        signup: "Jetzt testen",
        signupVariation: "Jetzt testen",
        pricing: "Preise",
        "user-menu": "Du",
        help: "FAQ&nbsp;&&nbsp;Hilfe",
        invite: "Freunde&nbsp;einladen",
        logout: "Abmelden",
        settings: "Einstellungen",
        wishlist: "Wunschliste",
        library: "Meine Titel",
        discover: "Entdecken",
        highlights: "Markierungen",
        upgrade: "Upgrade auf Premium",
        business: "Blinkist Business"
      }
    }
  }
};
</script>

<style lang="scss">
.header-link {
  $root: &;

  display: flex;
  align-items: center;

  cursor: pointer;

  &:not(#{$root}--cta) {
    @include font-style(button, $color: $midnight);

    &:hover {
      color: $blue;
    }

    &#{$root} {

      &--active {
        color: $green;

        #{$root}__menu-sprite {
          transform: scaleY(-1);
        }
      }

      &--main {
        position: relative;

        height: $spacing-24;
      }

      &--main#{&}--active,
      &--main:hover {
        color: inherit;

        &:after {
          content: "";

          position: absolute;

          bottom: -0.3rem;

          display: block;
          width: 100%;
          height: 2px;

          background-color: $green;
        }
      }
    }
  }

  &__menu-sprite {
    @include ease(transform, 0.15s);
  }
}
</style>
